import { track } from "@/utils/events/tracker";
import { toast } from "sonner";
import { updateCanvasStoryboards } from "@/store/supabaseSlice";
import { useDispatch } from "react-redux";
import { JsxElement } from "@/utils/jsx/jsxElementTypes";
import { useActiveCanvas, useActiveProject, useCanvasId } from "@/utils/utils";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useCallback, useEffect, useRef } from "react";
import { sanitizeComponentName } from "@/components/AppHeader/utils";
import { Storyboard } from "@/utils/types";
import { shouldUseAiGenerationSafe } from "@/utils/aiGenerationUtils";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import {
  InstantUpdateData,
  processInstantUpdateData,
} from "@/utils/navTreeUtils";
import {
  StoryboardDelta,
  UpdateStoryboardsChange,
  RemoveJsxChangeFields,
} from "@/store/zustand/changeLedgerTypes";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import { TempoElement } from "@/utils/tempoElement";
import { setSelectedElementKey } from "@/store/iframeSlice";
import { usePagesStore } from "@/store/zustand/pagesStore";
import { generateStoryboardFilename } from "@/utils/fileNameUtils";

export interface CreateStoryboardParams {
  storyboard: Storyboard;
  pageId: string;
  /**
   * One of fileContentsToCreateWith, componentToAdd, or fileContentsSourceFilename must be passed in.
   */
  fileContentsToCreateWith?: string;

  /**
   * Component to add, which can be either an object with optional properties
   * or a JsxElement.
   */
  componentToAdd?:
    | {
        componentId?: string;
        nativeTag?: string;
        propsToSet?: any;
      }
    | JsxElement;

  /**
   * This is an additional optional parameter for creating with file contents.
   */
  fileContentsSourceFilename?: string;

  /**
   * If set, scrolls the canvas to the storyboard.
   */
  scrollToStoryboard?: boolean;

  /**
   * Action text for logging purposes.
   */
  actionText?: string;

  /**
   * Additional files to include, mapped by their paths.
   */
  extraPathToContents?: {
    [path: string]: string;
  };

  /**
   * Must be provided, so the storyboard shows something immediately
   *
   * Nav tree is option, if not provided will make it from the html
   */
  instantUpdateData?: InstantUpdateData;

  /**
   * If true, will edit the text of the storyboard after it is created.
   */
  editTextAfterCreate?: boolean;

  /**
   * Codebase IDs to remove from the source after creating the storyboard.
   */
  sourceCodebaseIdsToRemove?: string[];
}

export const MIN_STORYBOARD_WIDTH = 1;
export const MIN_STORYBOARD_HEIGHT = 1;
export const MIN_STORY_HEIGHT = 1;
export const MIN_STORY_WIDTH = 1;
export const MAX_STORYBOARD_WIDTH = 10000;
export const MAX_STORYBOARD_HEIGHT = 10000;

export const useCreateStoryboard = () => {
  const canvasId = useCanvasId();

  const project = useActiveProject();
  const projectRef = useRef(project);
  useEffect(() => {
    projectRef.current = project;
  }, [project]);

  const canvas = useActiveCanvas();
  const canvasRef = useRef(canvas);
  useEffect(() => {
    canvasRef.current = canvas;
  }, [canvas]);
  const pages = usePagesStore((state) => state.pages);

  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);

  const dispatch = useDispatch();

  return useCallback(
    async ({
      storyboard,
      pageId,
      fileContentsToCreateWith,
      componentToAdd,
      fileContentsSourceFilename,
      scrollToStoryboard,
      actionText,
      extraPathToContents,
      instantUpdateData,
      editTextAfterCreate,
      sourceCodebaseIdsToRemove,
    }: CreateStoryboardParams): Promise<Storyboard | null> => {
      if (!supabaseClient) {
        console.error("Supabase client not available");
        return null;
      }

      if (!canvasRef.current) {
        console.error("Canvas not available");
        return null;
      }

      const treeElementLookup = useCodebaseStore.getState().treeElementLookup;

      track("STORYBOARD_CREATE", {
        creatingWithContents: Boolean(fileContentsToCreateWith),
        creatingWithComponent: Boolean(componentToAdd),
        type: storyboard.type,
      });

      const minWidth =
        storyboard.type === STORYBOARD_TYPES.STORY
          ? MIN_STORY_WIDTH
          : MIN_STORYBOARD_WIDTH;
      const minHeight =
        storyboard.type === STORYBOARD_TYPES.STORY
          ? MIN_STORY_HEIGHT
          : MIN_STORYBOARD_HEIGHT;

      if (storyboard.width < minWidth) {
        storyboard.width = minWidth;
      }

      if (storyboard.height < minHeight) {
        storyboard.height = minHeight;
      }

      const {
        html: htmlForInstantUpdate,
        navTree,
        error,
      } = processInstantUpdateData({
        storyboardId: storyboard.id,
        canvasId,
        projectSubfolderPath: projectRef.current?.subfolderPath,
        instantUpdateData,
        wrapInShell: true,
      });

      if (error) {
        toast.error(error);
        return null;
      }

      const body = {
        componentName: sanitizeComponentName(storyboard.name || ""),
      };

      // Compute destination file path if we have a page config path
      const pageConfigPath = usePagesStore
        .getState()
        .getPageSourcePath(canvasId, pageId);
      if (pageConfigPath) {
        const pageFolder = pageConfigPath.substring(
          0,
          pageConfigPath.lastIndexOf("/"),
        );
        const extension = ".tsx";
        const filename = generateStoryboardFilename(
          storyboard.name || "",
          storyboard.id,
          extension,
        );
        body["destinationFilePath"] = `${pageFolder}/${filename}`;
      }

      const castComponentToAdd = componentToAdd as any;

      if (castComponentToAdd) {
        // This means it's a jsx element
        if (castComponentToAdd.type) {
          body["addJsxBody"] = {
            sourceElement: componentToAdd,
            useAiGeneration: shouldUseAiGenerationSafe(
              componentToAdd as JsxElement,
            ),
          };
        } else {
          const sourceElement = castComponentToAdd.nativeTag
            ? {
                componentName: castComponentToAdd.nativeTag,
                type: "native",
              }
            : treeElementLookup[castComponentToAdd.componentId];
          body["addJsxBody"] = {
            sourceElement: sourceElement,
            propsToSet: castComponentToAdd.propsToSet,
            useAiGeneration: castComponentToAdd.componentId
              ? shouldUseAiGenerationSafe(
                  treeElementLookup[castComponentToAdd.componentId],
                )
              : false,
          };
        }
      } else if (fileContentsToCreateWith) {
        body["fileContents"] = fileContentsToCreateWith;
        body["fileContentsSourceFilename"] = fileContentsSourceFilename;
      }

      if (extraPathToContents) {
        body["extraFiles"] = extraPathToContents;
      }

      // Prepare removeJsxChangeFields with codebase IDs (conversion to JSX elements happens in prepareApiRequest)
      let removeJsxChangeFields: RemoveJsxChangeFields | undefined;
      if (sourceCodebaseIdsToRemove && sourceCodebaseIdsToRemove.length > 0) {
        removeJsxChangeFields = {
          codebaseIdsToRemove: sourceCodebaseIdsToRemove,
        };
      }

      // For now turn off
      // TODO: Re-enable this and tune it to work well
      // If you just try to enable you will see some flashing & reloading issues
      body["isInstantUpdate"] = Boolean(htmlForInstantUpdate && navTree);

      const change = new UpdateStoryboardsChange({
        deltas: [
          {
            changeType: "upsert",
            pageConfigPath,
            pageId,
            storyboardId: storyboard.id,
            storyboardUpdateDict: storyboard,
          },
        ],
        createStoryboardFields: {
          createStoryboardBody: body,
          editTextAfterCreate,
          navTreeForInstantUpdate: navTree,
          htmlForInstantUpdate: htmlForInstantUpdate,
          removeJsxChangeFields,
        },
        canvasId: canvasRef.current.id,
      });

      // // Auto-mark it as failed because we don't support these as instant updates right now (see above)
      // change.instantUpdateFailed = true;

      const getComponentDataFromRes = (
        data?: any,
      ): {
        error?: string;
        fullFilePath?: string;
        componentCodebaseId?: string;
      } => {
        const treeElements = data?.treeElementLookup;
        if (!treeElements || !Object.keys(treeElements).length) {
          return {
            error: `Error creating storyboard, no main component found. Please make sure the code is correct for ${
              actionText || "creation"
            }`,
          };
        }

        let createdComponent: any = Object.values(treeElements).find(
          (treeElement: any) =>
            treeElement.type === "component" &&
            treeElement.componentName === storyboard.name,
        );

        if (!createdComponent) {
          // Match the first component that is the default export
          createdComponent = Object.values(treeElements).find(
            (treeElement: any) =>
              treeElement.type === "component" && treeElement.defaultExport,
          );
        }

        const fullFilePath = createdComponent
          ? createdComponent.filename
          : // @ts-ignore
            Object.values(treeElements)[0].filename;

        if (!fullFilePath) {
          return { error: "Error creating storyboard: No file path found" };
        }

        return {
          fullFilePath,
          componentCodebaseId: createdComponent?.codebaseId || null,
        };
      };

      change.setResponseValidator((data) => {
        const { error } = getComponentDataFromRes(data);
        if (error) {
          return error;
        }
        return null;
      });

      try {
        const data = await useDesignCommandManager
          .getState()
          .executeCommandAsPromise(change);

        const { fullFilePath, componentCodebaseId } =
          getComponentDataFromRes(data);

        const storyboardUpdateDict = {
          created: true,
          created_at: new Date().toISOString(),
          fullFilePath,
          scrollToStoryboard: Boolean(scrollToStoryboard),

          // If instant update, no need to wait for the server to be ready
          createdHotReloadTs: data.wasInstantUpdate
            ? undefined
            : data.hotReloadTs,
        };

        const storyboardDeltas: StoryboardDelta[] = [
          {
            changeType: "update",
            pageId,
            pageConfigPath,
            storyboardId: storyboard.id,
            storyboardUpdateDict: storyboardUpdateDict,
          },
        ];

        const fullStoryboardDict = {
          ...storyboard,
          ...storyboardUpdateDict,
        };

        // Update the delta on the change so undo/redo works
        change.changeFields.deltas[0].storyboardUpdateDict = fullStoryboardDict;

        updateCanvasStoryboards(change.changeFields.canvasId, storyboardDeltas);

        dispatch(
          setSelectedElementKey(
            TempoElement.forStoryboard(fullStoryboardDict.id),
          ),
        );

        return fullStoryboardDict;
      } catch (error) {
        console.debug(
          "[useCreateStoryboard] Error creating storyboard:",
          error,
        );
        toast.error("Error creating storyboard");
        return null;
      }
    },
    [supabaseClient, canvasId, dispatch],
  );
};
